import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Stack,
  useClipboard,
} from '@chakra-ui/react';
import { magnifyValue } from '@diamond/shared/decimal-util-web';
import { BankIcon, Button, Heading, Icon, Text } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { InfoOutlined } from '@mui/icons-material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

import { PaymentMethodCardIcon } from './icons';

export interface MetodePembayaranProps {
  paymentMethod: string;
  rekening: string;
  accountHolder: string;
  bank: string;
  totalTagihanAkhir: number;
  totalDiff: number;
  onCopyValue: (value: string) => void;
  transactionId?: string;
  orderStatus?: string;
  termOfPaymentDays?: number | null;
}

export function CardMetodePembayaran({
  paymentMethod,
  rekening,
  accountHolder,
  bank,
  totalTagihanAkhir,
  totalDiff,
  onCopyValue,
  transactionId,
  orderStatus,
  termOfPaymentDays,
}: MetodePembayaranProps) {
  const {
    onCopy: onCopyRekening,
    value: valueRekening,
    hasCopied: hasCopiedRekening,
  } = useClipboard(String(rekening));
  const {
    onCopy: onCopyTotal,
    value: valueTotal,
    hasCopied: hasCopiedTotal,
  } = useClipboard(String(totalTagihanAkhir));
  const {
    onCopy: onCopyBeritaTransfer,
    value: valueBeritaTransfer,
    hasCopied: hasCopiedBeritaTransfer,
  } = useClipboard(String(transactionId ?? ''));

  const totalTagihanAkhirMagnified = magnifyValue(totalTagihanAkhir, 'idr');

  return (
    <Card
      shadow="none"
      borderRadius="8px"
      borderWidth="1px"
      borderColor="#D9D9D9"
    >
      <CardBody p={0}>
        <Accordion
          allowToggle
          defaultIndex={orderStatus === 'processed' ? undefined : [0]}
        >
          <AccordionItem border="none">
            <AccordionButton
              w={{ base: '100%', md: '378px' }}
              h="80px"
              p="24px"
              _hover={{ bg: 'gray.50' }}
              data-testid="accordion-button-payment-method"
            >
              <Flex alignItems="center" gap="2" flex={1}>
                <Icon
                  as={PaymentMethodCardIcon}
                  color="#0000A3"
                  boxSize="32px"
                />
                <Heading fontSize={{ base: 'lg', md: 'xl' }} fontWeight="600">
                  Metode Pembayaran
                </Heading>
              </Flex>
              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel
              px={{ base: 3, md: 4 }}
              pb={{ base: 3, md: 4 }}
              pt={0}
            >
              <Stack>
                <Stack pb={2}>
                  <Box
                    w="full"
                    h="1px"
                    bg="repeating-linear-gradient(to right, #D9D9D9 0 6px, transparent 6px 12px)"
                  />
                </Stack>
                {paymentMethod === 'cash_on_delivery' ? (
                  <Flex direction="column" gap="3">
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex direction="column" gap="1">
                        <Text
                          fontWeight="600"
                          fontSize="14px"
                          color="#0000A3"
                          lineHeight="100%"
                        >
                          Cash on Delivery
                        </Text>
                        <Text
                          fontWeight="400"
                          fontSize="14px"
                          color="#373F41"
                          lineHeight="20px"
                        >
                          Pembayaran setelah barang diterima
                        </Text>
                      </Flex>
                      <Box
                        w="78px"
                        h="24px"
                        borderRadius="8px"
                        px="2"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        border="1px solid transparent"
                        sx={{
                          background:
                            'linear-gradient(white, white) padding-box, linear-gradient(89.83deg, #FF1E1E 1.04%, #2451CE 77.66%, #5AC8FF 120.53%) border-box',
                        }}
                      >
                        <Text
                          fontWeight="900"
                          fontStyle="italic"
                          fontSize="14px"
                          lineHeight="100%"
                          letterSpacing="0.014em"
                          bgGradient="linear(89.83deg, #FF1E1E 1.04%, #2451CE 77.66%, #5AC8FF 120.53%)"
                          bgClip="text"
                        >
                          C.O.D
                        </Text>
                      </Box>
                    </Flex>
                    <Flex direction="column" gap="1">
                      <Text
                        fontWeight="400"
                        fontSize="12px"
                        lineHeight="100%"
                        color="gray.500"
                      >
                        Total Tagihan
                      </Text>
                      <Text
                        fontWeight="600"
                        fontSize="20px"
                        lineHeight="100%"
                        color="#373F41"
                      >
                        {buyerCurrencyFormatter(
                          totalTagihanAkhirMagnified.magnifiedValue
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                ) : paymentMethod === 'bank_transfer' ? (
                  <Stack>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      w={{ base: '54px', md: '105px' }}
                    >
                      <BankIcon bank={bank} inOrderDetail />
                    </Flex>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Stack spacing="0">
                          <Text
                            fontWeight="400"
                            fontSize="16px"
                            color="#8E8E93"
                          >
                            Nomor Rekening
                          </Text>
                          <Text
                            aria-label="nomor-rekening"
                            fontWeight="medium"
                            fontSize="20px"
                          >
                            {rekening}
                          </Text>
                        </Stack>
                        <Text fontSize="14px" fontWeight="semibold">
                          {`a/n ${accountHolder}`}
                        </Text>
                      </Box>
                      <Button
                        id="copy-rekening"
                        data-testid="copy-rekening"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyRekening();
                          onCopyValue(valueRekening);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedRekening ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Text fontWeight="500" fontSize="16px" color="#8E8E93">
                          Total Tagihan
                        </Text>
                        <Text
                          aria-label="total-tagihan"
                          fontWeight="600"
                          fontSize="20px"
                        >
                          {buyerCurrencyFormatter(
                            totalTagihanAkhirMagnified.magnifiedValue
                          )}
                        </Text>
                      </Box>
                      <Button
                        id="copy-total"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyTotal();
                          onCopyValue(valueTotal);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedTotal ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Text fontWeight="400" fontSize="16px" color="#8E8E93">
                          Berita atau Keterangan Transfer
                        </Text>
                        <Stack spacing="0">
                          <Text
                            aria-label="berita-transfer"
                            fontWeight="medium"
                            color="#373F41"
                            fontSize="20px"
                          >
                            {transactionId ?? '-'}
                          </Text>
                          <Text
                            fontWeight="400"
                            fontSize="12px"
                            color="#373F41"
                          >
                            Salin lalu tempel pada kolom berita / catatan /
                            keterangan transfer untuk verifikasi lebih cepat.
                          </Text>
                        </Stack>
                      </Box>
                      <Button
                        id="copy-berita-transfer"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyBeritaTransfer();
                          onCopyValue(valueBeritaTransfer);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedBeritaTransfer ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                  </Stack>
                ) : (
                  <Flex direction="column" gap="3">
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      gap="3"
                    >
                      <Flex direction="column" gap="1">
                        <Text
                          fontWeight="600"
                          fontSize="14px"
                          color="#0000A3"
                          lineHeight="100%"
                        >
                          Terms of Payment
                        </Text>
                        <Text
                          fontWeight="400"
                          fontSize="14px"
                          color="#373F41"
                          lineHeight="20px"
                        >
                          Pembayaran dengan termin
                        </Text>
                      </Flex>
                      <Box
                        w="78px"
                        h="24px"
                        borderRadius="8px"
                        px="2"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        border="1px solid transparent"
                        sx={{
                          background:
                            'linear-gradient(white, white) padding-box, linear-gradient(89.83deg, #FF1E1E 1.04%, #2451CE 77.66%, #5AC8FF 120.53%) border-box',
                        }}
                      >
                        <Text
                          fontWeight="900"
                          fontStyle="italic"
                          fontSize="14px"
                          lineHeight="100%"
                          letterSpacing="0.014em"
                          bgGradient="linear(89.83deg, #FF1E1E 1.04%, #2451CE 77.66%, #5AC8FF 120.53%)"
                          bgClip="text"
                        >
                          {termOfPaymentDays
                            ? `${termOfPaymentDays} hari`
                            : '-'}
                        </Text>
                      </Box>
                    </Flex>
                    <Flex direction="column" gap="1">
                      <Text
                        fontWeight="400"
                        fontSize="12px"
                        lineHeight="100%"
                        color="gray.500"
                      >
                        Total Tagihan
                      </Text>
                      <Text
                        fontWeight="600"
                        fontSize="20px"
                        lineHeight="100%"
                        color="#373F41"
                      >
                        {buyerCurrencyFormatter(
                          totalTagihanAkhirMagnified.magnifiedValue
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                )}
                {totalDiff !== 0 && (
                  <Flex
                    gap="2"
                    alignItems="center"
                    bg="#FEF3C7"
                    borderRadius="8px"
                    px={{ base: '2', md: '4' }}
                    py="2"
                  >
                    <Icon as={InfoOutlined} color="#F97316" />
                    <Text
                      fontSize={{ base: '2xs', md: 'sm' }}
                      fontWeight="bold"
                    >
                      {totalDiff < 0
                        ? `Potensi deposit ${buyerCurrencyFormatter(
                            Math.abs(totalDiff)
                          )}`
                        : `Potensi kurang bayar ${buyerCurrencyFormatter(
                            totalDiff
                          )}`}
                      <Text
                        as="span"
                        fontSize={{ base: '2xs', md: 'sm' }}
                        fontWeight="normal"
                      >
                        {' '}
                        karena perubahan stok atau produk.
                      </Text>
                    </Text>
                  </Flex>
                )}
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  );
}

export default CardMetodePembayaran;
